/**
 * The renewals register's wire layer — U5.5.
 *
 * ===========================================================================
 * TRANSCRIBED FROM THE SERVICE, NOT GUESSED
 * ===========================================================================
 * `RenewalRegisterRow` is copied field-for-field from
 * `apps/api/src/modules/renewals/renewals-register.service.ts`, and the envelope from
 * `renewals.controller.ts`'s `RenewalRegisterPage`.
 *
 * ===========================================================================
 * ⚠ MONEY *DOES* CROSS THIS BOUNDARY — UNLIKE EVERY OTHER REGISTER
 * ===========================================================================
 * `features/registers/api.ts` states in writing that no money crosses it, and that "if a
 * 'value' column ever appears on one of these registers, this comment is where the reader
 * learns that the screens were written on the assumption it could not." This is that
 * register, so the assumption is inverted HERE, in the file that carries the field:
 *
 * `line_total` is behind `amounts.view`. A seat without it receives the literal string
 * `'•••'` in that field — NOT null, NOT zero — so every consumer must go through
 * `isMaskedAmount` / `formatMoney` before it touches a number. `Number('•••')` is `NaN`, and
 * a renewals column quietly rendering NaN (or worse, 0) would tell a manager a contract is
 * worthless.
 *
 * ===========================================================================
 * `range` AND `today` ARE ANSWERS, NOT ECHOES
 * ===========================================================================
 * The screen asks for "this week"; the SERVER resolves that in the COUNTRY's calendar and
 * replies with the dates it actually used. The screen must label the table from THAT, never
 * from the browser clock: a viewer in a different timezone from the country they are looking
 * at would otherwise be wrong by a day for most of the day, on a screen whose entire job is
 * dates.
 */

import { ApiRequestError, ApiTransportError, apiRequest } from '../../lib/api';

/* ─────────────────────────────────────────────────────────────────── DTOs ── */

/**
 * The quick ranges — `renewal-ranges.ts`'s `RENEWAL_RANGES`.
 *
 * ⚠ MIRRORED, AND NOW GATED. This list drifted from the API's for as long as it has
 * existed and nothing said so: `range-mirror.test.ts` is the gate that reddens when the
 * two disagree, in the shape `source-mirror.test.ts` already uses one file over. Until it
 * was written, a range the server accepted was simply invisible here.
 */
export const RENEWAL_RANGES = [
  'today',
  'this_week',
  'next_week',
  'this_month',
  'next_month',
  'overdue',
  'custom',
] as const;
export type RenewalRange = (typeof RENEWAL_RANGES)[number];

/** `renewal-ranges.ts`'s `RENEWAL_STATUSES`, in the order an operator cares about them. */
export const RENEWAL_STATUSES = ['superseded', 'expired', 'due', 'upcoming'] as const;
export type RenewalStatus = (typeof RENEWAL_STATUSES)[number];

export const RENEWAL_SORTS = [
  'expiry',
  'customer',
  'product',
  'start',
  'value',
  'status',
  'owner',
] as const;
export type RenewalSort = (typeof RENEWAL_SORTS)[number];

/**
 * WHICH TABLE A CONTRACT CAME FROM — `renewal-windows.ts`'s `REMINDER_SOURCES`.
 *
 * ===========================================================================
 * ⚠ A LIST, NOT AN INLINE `'so' | 'quote'`, AND A MIRROR TEST HOLDS IT TO THE API
 * ===========================================================================
 * This union used to be typed inline on six interface fields across two files. Six
 * independent hand-written copies of a server vocabulary is drift with no gate: widening the
 * API's union could not move any of them, `pnpm --filter @crf/web typecheck` stayed green,
 * and the screens went on believing every non-SO contract was a quote.
 *
 * Declared once and pinned by `source-mirror.test.ts`, which reads the API's own source, the
 * same way `domain/countries.ts` and `domain/rbac.ts` are held to `@crf/shared`. That test
 * is a GATE, not a type: the two apps do not share a compilation unit, so the coupling
 * across the boundary is a red test rather than a red compile. Everything WITHIN this app is
 * compile-time — see {@link renewalDocumentPath}.
 */
export const RENEWAL_SOURCES = ['so', 'quote', 'project'] as const;
export type RenewalSource = (typeof RENEWAL_SOURCES)[number];

/**
 * What a drilldown OPENS — `renewal-source.ts`'s `RENEWAL_SOURCE_FACTS[…].documentType`.
 *
 * Separate from {@link RENEWAL_SOURCES} because they are separate questions and the server
 * answers both: `source` says which table the LINE is in, `document_type` says which screen
 * its parent DOCUMENT lives on. A Tally Sales subscription's document IS a quote (U5.4).
 *
 * ⚠ `'project'`, NOT `'project_maintenance'`, AND THE SERVER SETTLED THAT IN WRITING
 * (`renewal-source.ts`'s `project` row): `project_maintenance` is the LINE table — the
 * counterpart of `so_lines`, not of `sales_orders` — and a document type naming a line
 * table would send {@link renewalDocumentPath} looking for a screen that does not exist.
 */
export const RENEWAL_DOCUMENT_TYPES = ['sales_order', 'quote', 'project'] as const;
export type RenewalDocumentType = (typeof RENEWAL_DOCUMENT_TYPES)[number];

/**
 * Where a row's DOCUMENT lives, or `null` when this build cannot say.
 *
 * ===========================================================================
 * ⚠ THE `null` IS THE WHOLE POINT, AND IT IS THIS SCREEN'S OWN STATED RULE
 * ===========================================================================
 * The drilldown used to be `row.document_type === 'sales_order' ? /sales-orders/… :
 * /quotes/…` — a two-armed ternary whose `else` is reachable by anything that is not a sales
 * order. A third document type would have rendered a link to
 * `/countries/XX/quotes/<not-a-quote-id>`: the precise failure the comment beside it exists
 * to prevent — *"a dead link, which is worse than no link because the operator would believe
 * the document was missing."*
 *
 * So an unrecognised type produces NO LINK rather than a wrong one, and the caller renders
 * the document number as plain text. A blank screen would be worse than either.
 *
 * The refusal is COMPILE-TIME for anyone editing this app: {@link assertNeverDocumentType}
 * takes a `never`, so adding a member to {@link RENEWAL_DOCUMENT_TYPES} without a `case`
 * here fails `tsc` — the shape `apps/api/src/seeds/fy-label.ts` uses for the same reason one
 * union earlier. Returning `null` at runtime is the backstop for the other direction: a
 * server newer than the bundle.
 */
export function renewalDocumentPath(
  countryCode: string,
  documentType: RenewalDocumentType,
  documentId: string,
): string | null {
  switch (documentType) {
    case 'sales_order':
      return `/countries/${countryCode}/sales-orders/${documentId}`;
    case 'quote':
      return `/countries/${countryCode}/quotes/${documentId}`;
    /*
     * ⚠ THE PROJECT RECORD IS A `crfs` ROW, SO THE PATH IS THE CRF RECORD'S — and that is
     * not a fudge, it is the design's central verdict made visible. Projects C1 put
     * `work_type` on `crfs` rather than forking a `projects` table, so there is no
     * `/projects/:id` screen to link to and there must not be one. `document_id` on this
     * row is `project_maintenance.crf_id` (`live-contracts.ts`'s project half), i.e. a real
     * `crfs.id`, and `crfs/:crfId` is the record that opens.
     *
     * Linking to the QUOTE instead — which is what the retired two-armed ternary would have
     * done with this type, since anything not a sales order fell through to quotes — is the
     * dead link this function exists to refuse.
     */
    case 'project':
      return `/countries/${countryCode}/crfs/${documentId}`;
    default:
      return assertNeverDocumentType(documentType);
  }
}

/**
 * Exhaustiveness guard. A `never` PARAMETER rather than a `never` local so there is nothing
 * for `noUnusedLocals` to complain about and nothing to `void`.
 */
function assertNeverDocumentType(documentType: never): null {
  void documentType;
  return null;
}

/** `RenewalRegisterRow` — renewals-register.service.ts. */
export interface RenewalRegisterRow {
  /** Which table `line_id` indexes. `so_lines.id` 77 and `quote_lines.id` 77 differ. */
  source: RenewalSource;
  line_id: string;
  country_id: number;
  /** What the drilldown opens. A Tally Sales subscription's document IS a quote (U5.4). */
  document_type: RenewalDocumentType;
  document_id: string;
  document_doc_no: string;
  party_id: string;
  party_name: string;
  product_id: string;
  product_name: string;
  term_start: string | null;
  expiry_date: string;
  /** Negative when the contract has already lapsed. Computed by the server, in SQL. */
  days_remaining: number;
  /**
   * ⚠ `'•••'` for a seat without `amounts.view`. Never parse it — see the header.
   *
   * ⚠ AND `null` IS NOW REACHABLE, WHICH IT WAS NOT BEFORE THE THIRD SOURCE. Both sales
   * sources price their line, so `line_total` was non-NULL in practice and the omission
   * never showed. `project_maintenance.amount` is nullable — a term may be recorded before
   * anybody has priced the year — so a project row can legitimately carry no value.
   *
   * ⚠ THE THREE CASES ARE THREE DIFFERENT SENTENCES AND MUST NOT COLLAPSE: a figure, `•••`
   * ("you may not see it"), and `null` ("nobody has said"). Rendering `null` as `0` would
   * tell a manager the year is free; rendering it as `•••` would tell a seat that holds
   * `amounts.view` that it is being kept from them. The cell prints an em dash.
   */
  line_total: string | null;
  currency: string;
  status: RenewalStatus;
  owner_id: string | null;
  owner_name: string | null;
}

export interface ResolvedRange {
  key: RenewalRange;
  /** `null` only on `overdue`, which is deliberately unbounded below. */
  from: string | null;
  to: string | null;
}

export interface RenewalRegisterPage {
  data: RenewalRegisterRow[];
  page: number;
  perPage: number;
  /** The SERVER's count over the whole filtered set — never the length of `data`. */
  total: number;
  range: ResolvedRange;
  /** The COUNTRY's calendar day the server used. */
  today: string;
  sort: RenewalSort;
  dir: 'asc' | 'desc';
}

export interface RenewalRegisterFilters {
  range?: string;
  from?: string;
  to?: string;
  product?: string;
  /**
   * WHICH TABLE THE CONTRACT CAME FROM — the register's `?source=`, live since C7.
   *
   * ⚠ IT IS A NARROWING, NEVER A GRANT, and the server says so in the same words: this
   * filter composes with the per-seat visibility predicate, so `?source=so` shows a seat
   * without `so.view` nothing rather than showing it sales orders. Passed through
   * unvalidated for the reason the whole file gives below — a stranger value earns a 400
   * that NAMES the vocabulary, and silently dropping it here would render the whole
   * register while the chip claimed a filter.
   */
  source?: string;
  sort?: string;
  dir?: string;
  page?: number;
  perPage?: number;
}

/* ───────────────────────────────────────────────────────────────── fetchers ── */

/**
 * ⚠ FILTER VALUES ARE PASSED THROUGH UNVALIDATED, ON PURPOSE.
 *
 * The server owns every vocabulary here and answers a stranger with a 400 that NAMES the
 * allowed values. Silently dropping a bad `?range=` on the client would render the whole
 * register while the chips claimed it was filtered — the one wrong answer that looks right,
 * and on this screen it reads as "nothing expires soon".
 */
export async function fetchRenewalsRegister(
  countryCode: string,
  filters: RenewalRegisterFilters = {},
  signal?: AbortSignal,
): Promise<RenewalRegisterPage> {
  return apiRequest<RenewalRegisterPage>(`/countries/${countryCode}/renewals`, {
    query: {
      range: filters.range,
      from: filters.from,
      to: filters.to,
      product: filters.product,
      source: filters.source,
      sort: filters.sort,
      dir: filters.dir,
      page: filters.page,
      perPage: filters.perPage,
    },
    signal,
  });
}

/* ────────────────────────────────────────────────────────────────── errors ── */

/**
 * Re-authored per feature, the house pattern. A 403 never names the permission key; the
 * server's own wording is passed through on 400 because it is the only text that says what
 * to type instead — `range must be one of: today, this_week, …`.
 */
export function renewalsErrorText(error: unknown): string {
  if (error instanceof ApiTransportError) {
    return 'The server could not be reached. Nothing was changed.';
  }
  if (error instanceof ApiRequestError) {
    if (error.code === 'COUNTRY_SCOPE_VIOLATION') {
      return 'That register belongs to another country.';
    }
    if (error.status === 403) {
      return 'This account is not allowed to do that in this country.';
    }
    return error.message;
  }
  return 'Something went wrong. Nothing was changed.';
}

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const renewalsKeys = {
  register: (cc: string, filters: RenewalRegisterFilters) =>
    ['renewals', cc, 'register', filters] as const,
};
